"use client";

import { useCallback, type MutableRefObject, type Dispatch, type SetStateAction } from "react";
import {
  getVoiceToken as invokeGetVoiceToken,
  type PokerHubConnection,
  type TableHubEvents,
} from "@/lib/texas-holdem-signalr";
import type {
  ChatMessageDto,
  PlayerAction,
  PreActionType,
  ShowdownAction,
  UpdateSettingsRequest,
  HandHistoryListItem,
  HandHistoryDto,
  EndGameSummary,
  SessionBalancesResponse,
} from "@/lib/texas-holdem-types";

export interface PokerGameActionsDeps {
  tableRef: MutableRefObject<PokerHubConnection<TableHubEvents> | null>;
  playerIdRef: MutableRefObject<string | null>;
  tableInvoke: <T = void>(method: string, ...args: unknown[]) => Promise<T | undefined>;
  playerName: string;
  isSubmitting: boolean;
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  setIsLoadingHistory: Dispatch<SetStateAction<boolean>>;
  setChatMessages: Dispatch<SetStateAction<ChatMessageDto[]>>;
  setEndGameSummary: Dispatch<SetStateAction<EndGameSummary | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
}

export function usePokerGameActions(deps: PokerGameActionsDeps) {
  const {
    tableRef, playerIdRef, tableInvoke,
    playerName, isSubmitting,
    setIsSubmitting, setIsLoadingHistory, setChatMessages,
    setEndGameSummary, setError,
  } = deps;

  const selectSeat = useCallback(async (seatIndex: number, buyIn: number) => {
    if (!tableRef.current || isSubmitting) return false;
    setIsSubmitting(true);
    try {
      return await tableRef.current.invoke<boolean>("SelectSeat", seatIndex, buyIn);
    } catch {
      setError("Failed to select seat");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [tableRef, isSubmitting, setIsSubmitting, setError]);

  const startDeal = useCallback(async () => {
    if (!tableRef.current || isSubmitting) return false;
    setIsSubmitting(true);
    try {
      return await tableRef.current.invoke<boolean>("StartDeal");
    } catch {
      setError("Failed to deal");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [tableRef, isSubmitting, setIsSubmitting, setError]);

  const submitAction = useCallback(async (action: PlayerAction, amount = 0) => {
    if (!tableRef.current || isSubmitting) return false;
    setIsSubmitting(true);
    try {
      return await tableRef.current.invoke<boolean>("SubmitAction", { action, amount });
    } catch {
      setError("Failed to submit action");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [tableRef, isSubmitting, setIsSubmitting, setError]);

  const setPreAction = useCallback(async (preAction: PreActionType | null) => {
    if (!tableRef.current) return;
    try {
      await tableRef.current.invoke("SetPreAction", preAction);
    } catch {}
  }, [tableRef]);

  const sitOut = useCallback(async () => { await tableInvoke("SitOut"); }, [tableInvoke]);
  const sitIn = useCallback(async () => { await tableInvoke("SitIn"); }, [tableInvoke]);

  const rebuy = useCallback(async (amount: number) => {
    const result = await tableInvoke<boolean>("Rebuy", amount);
    return result ?? false;
  }, [tableInvoke]);

  const chooseShowdown = useCallback(async (action: ShowdownAction) => {
    const result = await tableInvoke<boolean>("ChooseShowdown", action);
    return result ?? false;
  }, [tableInvoke]);

  const voteRunItTwice = useCallback(async (agree: boolean) => {
    const result = await tableInvoke<boolean>("VoteRunItTwice", agree);
    return result ?? false;
  }, [tableInvoke]);

  const sendChat = useCallback(async (message: string) => {
    const optimistic: ChatMessageDto = {
      playerId: playerIdRef.current ?? "",
      playerName: playerName || "You",
      message,
      timestamp: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev.slice(-100), optimistic]);
    await tableInvoke("SendChat", message);
  }, [tableInvoke, playerIdRef, playerName, setChatMessages]);

  const togglePause = useCallback(async () => { await tableInvoke("TogglePause"); }, [tableInvoke]);
  const endGame = useCallback(async () => { await tableInvoke("EndGame"); }, [tableInvoke]);
  const updateSettings = useCallback(async (settings: UpdateSettingsRequest) => { await tableInvoke("UpdateSettings", settings); }, [tableInvoke]);
  const transferHost = useCallback(async (targetId: string) => { await tableInvoke("TransferHost", targetId); }, [tableInvoke]);
  const adjustChips = useCallback(async (targetId: string, amount: number) => { await tableInvoke("AdjustChips", targetId, amount); }, [tableInvoke]);
  const sendReaction = useCallback(async (targetSeat: number, emoji: string) => { await tableInvoke("SendReaction", targetSeat, emoji); }, [tableInvoke]);

  const ping = useCallback(async (): Promise<number> => {
    if (!tableRef.current) return -1;
    const start = Date.now();
    try {
      await tableRef.current.invoke<number>("Ping");
      return Date.now() - start;
    } catch {
      return -1;
    }
  }, [tableRef]);

  const getHandHistory = useCallback(async (skip = 0, take = 20): Promise<HandHistoryListItem[]> => {
    if (!tableRef.current) return [];
    setIsLoadingHistory(true);
    try {
      return await tableRef.current.invoke<HandHistoryListItem[]>("GetHandHistory", skip, take);
    } catch {
      return [];
    } finally {
      setIsLoadingHistory(false);
    }
  }, [tableRef, setIsLoadingHistory]);

  const getBalances = useCallback(async (): Promise<SessionBalancesResponse | null> => {
    if (!tableRef.current) return null;
    try {
      return await tableRef.current.invoke<SessionBalancesResponse>("GetBalances");
    } catch {
      return null;
    }
  }, [tableRef]);

  const clearEndGameSummary = useCallback(() => {
    setEndGameSummary(null);
  }, [setEndGameSummary]);

  const getVoiceToken = useCallback(async (): Promise<string> => {
    if (!tableRef.current) throw new Error("Not connected to table");
    return invokeGetVoiceToken(tableRef.current);
  }, [tableRef]);

  const getHandDetails = useCallback(async (handId: string): Promise<HandHistoryDto | null> => {
    if (!tableRef.current) return null;
    setIsLoadingHistory(true);
    try {
      return await tableRef.current.invoke<HandHistoryDto | null>("GetHandDetails", handId);
    } catch {
      return null;
    } finally {
      setIsLoadingHistory(false);
    }
  }, [tableRef, setIsLoadingHistory]);

  return {
    selectSeat,
    startDeal,
    submitAction,
    setPreAction,
    sitOut,
    sitIn,
    rebuy,
    chooseShowdown,
    voteRunItTwice,
    sendChat,
    togglePause,
    endGame,
    updateSettings,
    transferHost,
    adjustChips,
    sendReaction,
    ping,
    getHandHistory,
    getBalances,
    clearEndGameSummary,
    getVoiceToken,
    getHandDetails,
  };
}
